/**
 * The deployment's `NEXT_PUBLIC_*` values, read when the code runs rather than
 * when it is built.
 *
 * One image serves test1, test2, staging and production, so these values come
 * from the container's environment (written into the ECS task definition at
 * deploy). A literal `process.env.NEXT_PUBLIC_X` would be inlined by
 * `next build` as whatever the build machine had, in the browser bundle and the
 * server bundle alike, which is why every read goes through `publicEnv`.
 *
 * On the server this reads `process.env`. In the browser it reads
 * `window.__ENV`, which `PublicEnvScript` in the root layout writes from the
 * server's environment before any app module runs. The `process.env` fallback
 * in the browser only matters under Jest, where tests set the variables
 * directly.
 *
 * `NEXT_PUBLIC_BUILD_ID` and `NEXT_PUBLIC_APP_VERSION` are the same in every
 * environment and stay build-time literals (`public-env.guard.test.ts`).
 */

export type PublicEnvName = `NEXT_PUBLIC_${string}`;

declare global {
    interface Window {
        __ENV?: Record<string, string>;
    }
}

const PUBLIC_PREFIX = 'NEXT_PUBLIC_';

/**
 * Browser values whose variable has a server name. `next.config.mjs` used to
 * inline these into the browser bundle; serving them here keeps the contexts
 * and local env files unchanged.
 */
const SERVER_NAMED: Partial<Record<PublicEnvName, string>> = {
    NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN: 'MAPBOX_ACCESS_TOKEN',
};

function serverValue(name: PublicEnvName): string | undefined {
    const serverName = SERVER_NAMED[name];
    return process.env[name] ?? (serverName ? process.env[serverName] : undefined);
}

export function publicEnv(name: PublicEnvName): string | undefined {
    if (typeof window !== 'undefined') {
        const value = window.__ENV?.[name];
        if (value !== undefined) return value;
    }
    return serverValue(name);
}

/**
 * Every `NEXT_PUBLIC_*` value in this server's environment. Server only: the
 * root layout serialises it into `window.__ENV`. Never put a secret under a
 * `NEXT_PUBLIC_` name; everything here is sent to every browser.
 */
export function publicEnvSnapshot(): Record<string, string> {
    const snapshot: Record<string, string> = {};
    for (const [name, value] of Object.entries(process.env)) {
        if (name.startsWith(PUBLIC_PREFIX) && value !== undefined) {
            snapshot[name] = value;
        }
    }
    for (const name of Object.keys(SERVER_NAMED) as PublicEnvName[]) {
        const value = serverValue(name);
        if (value !== undefined) snapshot[name] = value;
    }
    return snapshot;
}

/** `window.__ENV = {...}`, safe to place inside a `<script>` element. */
export function publicEnvScriptSource(snapshot: Record<string, string>): string {
    return `window.__ENV=${JSON.stringify(snapshot).replace(/</g, '\\u003c')};`;
}
